import {
  NEARLY_INFINITY as NINF,
  INFINITY as INF,
  governingComponentCalculators,
  responseCalculators,
  componentCalculators,
  specsCalculatorsFromComponents,
} from './internals.js';
import * as ESeries from './e-series.js';

export const NEARLY_INFINITY = NINF; /* Provided for backwards-compatibility support. DO NOT USE. */
export const INFINITY = INF;

/******************** Export E series functions.  These could theoretically be their own library ***************/
export function getSeriesValuesBetween(
  min, //: number,
  max, //: number,
  series // : "E6" | "E12" | "E24" | "E96"
) {
  return ESeries.getSeriesValuesBetween(min, max, series);
}

export function normalizeToPreferredSeries(
  idealValue, //: number,
  series, //: "E6" | "E12" | "E24" | "E48" | "E96",
  options // { forceRoundDirection: "up" |"down" | "closest" },
) {
  return ESeries.normalizeToPreferredSeries(idealValue, series, options);
}

// Vestigial method to support migration over time of implementation naming
let canonicalizeImplementation = function (suppliedImplementation) {
  let result = suppliedImplementation;
  return result;
};

/**
 * getGoverningCapacitorLimits - Get the minimum and maximum feasible values for the governing capacitor of the filter with the specified properties
 *
 * @param {"lowpass"|"bandpass"|"highpass|"lowpassV2"} type - The type of filter
 * @param {"passive" | "firstOrder" | "multipleFeedback" | "sallenKey" | "inductor" | "inductorPassive" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor" | "activeInductor" } implementation - The filter implementation used
 * @param {number} fp - The corner frequency of the filter (Hz)
 * @param {number} gain - The gain of the filter (V/V)
 * @param {number} q - The q of the filter - ignored on passive and firstOrder filters
 * @returns {ComponentRange | undefined} - The range of component values {min, max} - undefined if there are parameter errors
 */
export function getGoverningCapacitorLimits(
  type, //: "lowpass"|"bandpass"|"highpass"|"lowpassV2",
  implementation, //: "multipleFeedback" | "sallenKey" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor" | "activeInductor",
  fp, //: number  // Hz
  gain, //: number  // V/V
  q //: number  // unitless - not used on passive and firstOrder
) {
  //: ComponentRange
  //const RMIN = 1;
  const RFLOADMIN = 20;
  const RLOADMIN = 50;
  const RMAX = 10e6;
  const CMIN = 10e-12;
  const CMAX = 10e-6;
  const LMIN = 1e-9;
  const LMAX = 33e-6;
  let minGov;

  let _implementation = canonicalizeImplementation(implementation);

  if ((_implementation === 'activeInductor' || _implementation === 'passiveInductor') && type === 'bandpass') {
    minGov = [CMIN * 2];
  } else {
    minGov = [CMIN];
  }
  let maxGov = [CMAX];
  if (!governingComponentCalculators['singleEnded'][_implementation]) {
    return;
  }
  if (!governingComponentCalculators['singleEnded'][_implementation][type]) {
    return;
  }
  let getCapacitor = governingComponentCalculators['singleEnded'][_implementation][type];
  for (let part of ['R1', 'R2', 'R5', 'R1pR2', 'R2pR5']) {
    if (getCapacitor[part]) {
      let mintype = RLOADMIN;
      if (
        (_implementation === 'activeInductor' && part === 'R1') ||
        (_implementation === 'passiveInductor' && part === 'R1')
      ) {
        mintype = RFLOADMIN;
      }
      //TODO: Enumerate the resistors of various implementations that can afford to use RMIN_NONLOAD
      let capacitor1 = getCapacitor[part](mintype, fp, q, gain);
      let capacitor2 = getCapacitor[part](RMAX, fp, q, gain);
      if (capacitor1 && capacitor2) {
        if (capacitor1 < capacitor2) {
          minGov.push(capacitor1);
          maxGov.push(capacitor2);
        } else {
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
      }
    }
  }
  for (let part of ['C1', 'C2', 'C3']) {
    if (getCapacitor[part]) {
      let capacitor1 = getCapacitor[part](CMIN, fp, q, gain);
      let capacitor2 = getCapacitor[part](CMAX, fp, q, gain);
      if (capacitor1 < capacitor2) {
        minGov.push(capacitor1);
        maxGov.push(capacitor2);
      } else {
        maxGov.push(capacitor1);
        minGov.push(capacitor2);
      }
    }
  }
  for (let part of ['L1']) {
    if (getCapacitor[part]) {
      let capacitor1 = getCapacitor[part](LMIN, fp, q, gain);
      let capacitor2 = getCapacitor[part](LMAX, fp, q, gain);
      if (capacitor1 < capacitor2) {
        minGov.push(capacitor1);
        maxGov.push(capacitor2);
      } else {
        maxGov.push(capacitor1);
        minGov.push(capacitor2);
      }
    }
  }

  return { min: Math.max(...minGov), max: Math.min(...maxGov) };
}
/**
 * getGoverningComponentLimitsDiffGround - Get the minimum and maximum feasible values for the governing capacitor of the filter with the specified properties
 *
 * @param {"lowpass"|"bandpass"|"highpass"} type - The type of filter
 * @param {"multipleFeedback" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor"} implementation - The filter implementation used
 * @param {number} fp - The corner frequency of the filter (Hz)
 * @param {number} gain - The gain of the filter (V/V)
 * @param {number} q - The q of the filter - ignored on passive and active firstOrder filters
 * @param {number} crossedValue - input of crossed capacitor (normally c3) or crossed resistor (normally r3)
 * @returns {ComponentRange | undefined} - The range of component values {min, max} - undefined if there are parameter errors
 */
export function getGoverningComponentLimitsDiffGround(
  type, //: "lowpass"|"bandpass"|"highpass",
  implementation, //: "multipleFeedback" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor",
  fp, //: number  // Hz
  gain, //: number  // V/V
  q, //: number  // unitless - not used on passive and active firstOrder
  crossedValue // : number // F - input of crossed capacitor (normally c3) or Ohms - input of crossed resistor (normally r3), leave undefined if no crossed component
) {
  //: ComponentRange
  //const RMIN = 1;
  const RFLOADMIN = 20;
  const RLOADMIN = 50;
  const RMAX = 10e6;
  const CMIN = 10e-12;
  const CMAX = 10e-6;
  const LMIN = 1e-9;
  const LMAX = 33e-6;
  let minGov, maxGov;
  let _implementation = canonicalizeImplementation(implementation);

  if (!governingComponentCalculators['differential']) {
    return;
  }
  if (!governingComponentCalculators['differential'][_implementation]) {
    return;
  }
  if (!governingComponentCalculators['differential'][_implementation][type]) {
    return;
  }
  if (!governingComponentCalculators['differential'][_implementation][type]['crossed']) {
    return;
  }
  if (_implementation === 'passiveInductor' && type === 'bandpass') {
    minGov = [CMIN * 2];
    maxGov = [CMAX];
  } else if (_implementation === 'passiveFirstOrder' && type === 'highpass') {
    minGov = [RLOADMIN];
    maxGov = [RMAX];
  } else {
    minGov = [CMIN];
    maxGov = [CMAX];
  }
  if (_implementation === 'passiveFirstOrder' && type === 'highpass') {
    let getResistor = governingComponentCalculators['differential'][_implementation][type]['grounded'];
    let resistor1 = getResistor['C1'](CMIN, fp, q, gain, INF); // should always be the max gov
    let resistor2 = getResistor['C1'](CMAX, fp, q, gain, INF); // should always be the min gov
    maxGov.push(resistor1);
    minGov.push(resistor2);
    resistor1 = getResistor['C1'](CMIN, fp, q, gain, crossedValue); // should always be the max gov
    resistor2 = getResistor['C1'](CMAX, fp, q, gain, crossedValue); // should always be the min gov
    resistor1 < 0 ? maxGov.push(RMAX) : maxGov.push(resistor1);
    minGov.push(resistor2);
  } else {
    let getCapacitor = governingComponentCalculators['differential'][_implementation][type]['grounded'];

    for (let part of ['R1', 'R2', 'R5', 'R1pR2', 'R2pR5']) {
      if (getCapacitor[part]) {
        let mintype = RLOADMIN;
        if (_implementation === 'passiveInductor' && part === 'R1') {
          mintype = RFLOADMIN;
        }
        if ((_implementation === 'passiveFirstOrder' && type === 'lowpass') || _implementation === 'passiveInductor') {
          let capacitor1 = getCapacitor[part](mintype, fp, q, gain, 0);
          let capacitor2 = getCapacitor[part](RMAX, fp, q, gain, 0);
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
        //TODO: Enumerate the resistors of various implementations that can afford to use RMIN_NONLOAD
        let capacitor1 = getCapacitor[part](mintype, fp, q, gain, crossedValue);
        let capacitor2 = getCapacitor[part](RMAX, fp, q, gain, crossedValue);
        if (capacitor1 && capacitor2) {
          if (capacitor1 < capacitor2) {
            minGov.push(capacitor1);
            maxGov.push(capacitor2);
          } else {
            maxGov.push(capacitor1);
            minGov.push(capacitor2);
          }
        }
      }
    }
    for (let part of ['C1', 'C2', 'C3']) {
      if (getCapacitor[part]) {
        let capacitor1 = getCapacitor[part](CMIN, fp, q, gain, crossedValue);
        let capacitor2 = getCapacitor[part](CMAX, fp, q, gain, crossedValue);
        if (capacitor1 < capacitor2) {
          minGov.push(capacitor1);
          maxGov.push(capacitor2);
        } else {
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
      }
    }
    for (let part of ['L1', 'L3']) {
      if (getCapacitor[part]) {
        if ((_implementation === 'passiveFirstOrder' && type === 'lowpass') || _implementation === 'passiveInductor') {
          let capacitor1 = getCapacitor[part](LMIN, fp, q, gain, 0);
          let capacitor2 = getCapacitor[part](LMAX, fp, q, gain, 0);
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
        let capacitor1 = getCapacitor[part](LMIN, fp, q, gain, crossedValue);
        let capacitor2 = getCapacitor[part](LMAX, fp, q, gain, crossedValue);
        if (capacitor1 < capacitor2) {
          minGov.push(capacitor1);
          maxGov.push(capacitor2);
        } else {
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
      }
    }
  }

  return { min: Math.max(...minGov), max: Math.min(...maxGov) };
}

/**
 * getGoverningComponentLimitsDiffCross - Get the minimum and maximum feasible values for the governing capacitor of the filter with the specified properties
 *
 * @param {"lowpass"|"bandpass"|"highpass"} type - The type of filter
 * @param {"multipleFeedback" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor"} implementation - The filter implementation used
 * @param {number} fp - The corner frequency of the filter (Hz)
 * @param {number} gain - The gain of the filter (V/V)
 * @param {number} q - The q of the filter - ignored on firstOrder filters
 * @param {number} groundedValue - F - input ground capacitors or ohms - input ground resistors
 * @returns {ComponentRange | undefined} - The range of component values {min, max} - undefined if there are parameter errors
 */
export function getGoverningComponentLimitsDiffCross(
  type, //: "lowpass"|"bandpass"|"highpass",
  implementation, //: "multipleFeedback" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor",
  fp, //: number  // Hz
  gain, //: number  // V/V
  q, //: number  // unitless - not used on passive and active firstOrder
  groundedValue //: number // F - input ground capacitors or ohms - input ground resistors
) {
  //: ComponentRange
  //const RMIN = 1;
  const RFLOADMIN = 20;
  const RLOADMIN = 50;
  const RMAX = 10e6;
  const CMIN = 10e-12;
  const CMAX = 10e-6;
  const LMIN = 1e-9;
  const LMAX = 33e-6;
  let minGov, maxGov;
  let _implementation = canonicalizeImplementation(implementation);

  if (!governingComponentCalculators['differential']) {
    return;
  }
  if (!governingComponentCalculators['differential'][_implementation]) {
    return;
  }
  if (!governingComponentCalculators['differential'][_implementation][type]) {
    return;
  }
  if (!governingComponentCalculators['differential'][_implementation][type]['crossed']) {
    return;
  }
  if (_implementation === 'passiveInductor' && type === 'bandpass') {
    minGov = [CMIN * 2];
    maxGov = [CMAX];
  } else if (_implementation === 'passiveFirstOrder' && type === 'highpass') {
    minGov = [RLOADMIN];
    maxGov = [RMAX];
  } else {
    minGov = [CMIN];
    maxGov = [CMAX];
  }
  if (_implementation === 'passiveFirstOrder' && type === 'highpass') {
    let getResistor = governingComponentCalculators['differential'][_implementation][type]['grounded'];
    let resistor1 = getResistor['C1'](CMIN, fp, q, gain, INF); // should always be the max gov
    let resistor2 = getResistor['C1'](CMAX, fp, q, gain, INF); // should always be the min gov
    maxGov.push(resistor1);
    minGov.push(resistor2);
    resistor1 = getResistor['C1'](CMIN, fp, q, gain, groundedValue); // should always be the max gov
    resistor2 = getResistor['C1'](CMAX, fp, q, gain, groundedValue); // should always be the min gov
    resistor1 < 0 ? maxGov.push(RMAX) : maxGov.push(resistor1);
    minGov.push(resistor2);
  } else {
    let getCapacitor = governingComponentCalculators['differential'][_implementation][type]['crossed'];
    for (let part of ['R1', 'R2', 'R5', 'R1pR2', 'R2pR5']) {
      if (getCapacitor[part]) {
        let mintype = RLOADMIN;
        if (_implementation === 'passiveInductor' && part === 'R1') {
          mintype = RFLOADMIN;
        }
        //TODO: Enumerate the resistors of various implementations that can afford to use RMIN_NONLOAD
        if ((_implementation === 'passiveFirstOrder' && type === 'lowpass') || _implementation === 'passiveInductor') {
          let capacitor1 = getCapacitor[part](mintype, fp, q, gain, 0);
          let capacitor2 = getCapacitor[part](RMAX, fp, q, gain, 0);
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
        let capacitor1 = getCapacitor[part](mintype, fp, q, gain, groundedValue);
        let capacitor2 = getCapacitor[part](RMAX, fp, q, gain, groundedValue);
        if (capacitor1 && capacitor2) {
          if (capacitor1 < capacitor2) {
            minGov.push(capacitor1);
            maxGov.push(capacitor2);
          } else {
            maxGov.push(capacitor1);
            minGov.push(capacitor2);
          }
        }
      }
    }
    for (let part of ['C1', 'C2', 'C3']) {
      if (getCapacitor[part]) {
        let capacitor1 = getCapacitor[part](CMIN, fp, q, gain, groundedValue);
        let capacitor2 = getCapacitor[part](CMAX, fp, q, gain, groundedValue);
        if (capacitor1 < capacitor2) {
          minGov.push(capacitor1);
          maxGov.push(capacitor2);
        } else {
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
      }
    }
    for (let part of ['L1', 'L3']) {
      if (getCapacitor[part]) {
        if ((_implementation === 'passiveFirstOrder' && type === 'lowpass') || _implementation === 'passiveInductor') {
          let capacitor1 = getCapacitor[part](LMIN, fp, q, gain, 0);
          let capacitor2 = getCapacitor[part](LMAX, fp, q, gain, 0);
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
        let capacitor1 = getCapacitor[part](LMIN, fp, q, gain, groundedValue);
        let capacitor2 = getCapacitor[part](LMAX, fp, q, gain, groundedValue);
        if (capacitor1 < capacitor2) {
          minGov.push(capacitor1);
          maxGov.push(capacitor2);
        } else {
          maxGov.push(capacitor1);
          minGov.push(capacitor2);
        }
      }
    }
  }
  return { min: Math.max(...minGov), max: Math.min(...maxGov) };
}

/**
 * getAbstractFilterResponse - Get the response (magnitude and phase) of the filter with the specified properties
 *
 * @param {"lowpass" | "bandpass" | "highpass"} type - The type of filter
 * @param { 1 | 2} order - The order of filter
 * @param {number} fp - The filter frequency (Hz)
 * @param {number} gain - The filter gain (V/V)
 * @param {number} q - The q of the filter - ignored on passive and active firstOrder filters
 * @param {number[]} frequencies - Frequencies at which to deliver response (Hz)
 * @returns {FilterResponse[] | undefined}  - The response at each of the supplied frequencies. Undefined if parameter checking fails.
 */
export function getAbstractFilterResponse(
  type, // : "lowpass" | "bandpass" | "highpass",
  order, // : 1 | 2,
  fp, // : number, // Hz
  gain, // : number, // V/V
  q, // : number, // unitless - not used on passive and active firstOrder
  frequencies //: number[] // Hz
) {
  // : FilterResponse[] | undefined
  // Parameter checking
  if (
    typeof responseCalculators[type] === 'undefined' ||
    (order !== 1 && order !== 2) ||
    typeof responseCalculators[type][order - 1] === 'undefined' ||
    typeof fp !== 'number' ||
    !isFinite(fp) ||
    fp != fp || //safer equivalent to isNan(fp)
    typeof gain !== 'number' ||
    !isFinite(gain) ||
    gain != gain ||
    typeof q !== 'number' ||
    !isFinite(q) ||
    q != q ||
    !Array.isArray(frequencies)
  ) {
    return;
  }
  // Let's get to it...
  return frequencies.map((frequency) => {
    let complexGain = responseCalculators[type][order - 1](fp, gain, q, frequency);
    let magnitude = Math.sqrt(complexGain.real * complexGain.real + complexGain.imaginary * complexGain.imaginary);
    let phase = Math.atan2(complexGain.imaginary, complexGain.real);
    return {
      magnitudeVV: magnitude,
      magnitudedB: 20 * Math.log10(magnitude),
      phaseRadians: phase,
      phaseDegrees: (phase * 180) / Math.PI,
    };
  });
}

/**
 * getMovingAverageFilterResponse - Get the response (magnitude and phase) for a moving average (boxcar) lowpass filter
 * Math from: https://dsp.stackexchange.com/questions/28296/phase-response-of-moving-average-filter-how-to-interpret
 * @param {number} numberOfTaps - The length of the boxcar filter
 * @param {number} sampleRate - Sampling rate of digital data (sps)
 * @param {number[]} frequencies - Frequencies at which to deliver response (Hz)
 * @returns {FilterResponse[] | undefined}  - The response at each of the supplied frequencies. Undefined if parameter checking fails.
 */
export function getMovingAverageFilterResponse(
  numberOfTaps, //number of taps: 2 to infinity
  sampleRate, //Sampling rate of digital data // sps
  frequencies //: number[] // Hz
) {
  if (numberOfTaps <= 1 || sampleRate <= 0 || !Array.isArray(frequencies)) {
    return undefined;
  }

  //Delay of filter in seconds.
  //Imagine you are sitting at the output of the bitstream with a computer that can do math infinitely fast.  So you only have to wait on the samples, not on the math.
  //Let's say you want a 1 tap filter.  i.e. no filter at all.  You return the sample you just got.  There is no delay at all.
  //Now let's say you want a 5 tap filter.  You need to average the sample you just got with the 2 previous samples as well as the two samples to come.  So the time you need to wait is 2 samples worth.
  //Extrapolating to any odd filter, the time you need to wait is (numberOfTaps - 1) / 2.
  //Now let's say you want a six tap filter.  Timewise, the average output lands exactly in the middle of your sample 3 and sample 4.  So at that point in time, you need to wait 2.5 more samples before you have all the data you need.
  //So math still works for even case, but you are no longer synchronized with the original digital bit stream clock (you are half a sample off).
  let delayOfFilterInSamplePeriods = (-1 * (numberOfTaps - 1)) / 2;
  let delayOfFilterInSeconds = delayOfFilterInSamplePeriods / sampleRate;

  return frequencies.map((f) => {
    let normalizedFrequency = (Math.PI * f) / sampleRate;
    let magnitude = Math.abs(
      Math.sin(numberOfTaps * normalizedFrequency) / (numberOfTaps * Math.sin(normalizedFrequency))
    );
    //Delay of filter as a fraction of the frequency's period
    let delayAsFractionOfPeriod = delayOfFilterInSeconds * f;
    return {
      magnitudeVV: magnitude,
      magnitudedB: 20 * Math.log10(magnitude),
      phaseRadians: delayAsFractionOfPeriod * 2 * Math.PI,
      phaseDegrees: delayAsFractionOfPeriod * 360,
    };
  });
}

/**
 * calculateFilterComponents - Calculate the passive components required to implement the specified filter.
 *
 * @param {"lowpass"|"bandpass"|"highpass"|"lowpassV2"} type
 * @param {"multipleFeedback" | "sallenKey" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor" | "activeInductor"} implementation
 * @param {number} fp - Filter frequency (Hz)
 * @param {number} gain - Filter gain (V/V)
 * @param {number} q - Filter q - not used on passive and active firstOrder
 * @param {number} cGov - Governing capacitor - C1, except C2 in multipleFeedback highpass (F)
 * @param {number} rGainOrCCross - Total gain resistance - R3+R4 (Ω) or crossover capacitor / resistor for differential circuits (typically C3 or R3)
 * @param {"singleEnded" | "differential"} input - default "singleEnded"
 * @param {'E6' | 'E12' | 'E24' | 'E48' | 'E96'} capESeries - The eSeries of the capacitor slider.  Ignored for everything except Sallen Key Lowpass.
 * @returns {FilterPassives | undefined} The set of passive values. Values unused in the circuit are returned as undefined. Undefined if there are parameter errors.
 */
export function calculateFilterComponents(
  type, //: "lowpass"|"bandpass"|"highpass"|"lowpassV2",
  implementation, //: "multipleFeedback" | "sallenKey" | "passiveFirstOrder" | "activeFirstOrder" | "passiveInductor" | "activeInductor",
  fp, //: number  // Hz
  gain, //: number  // V/V
  q, //: number  // unitless - not used on passive and active firstOrder
  cGov, //: number // F - governing capacitor (typically C1) or ohms - governing resistor (R1 in differential passive highpass)
  rGainOrCCross, //: number  // Ω - total gain resistance (R3+R4) or crossover capacitor / resistor for differential circuits (typically C3)
  input = 'singleEnded', //: "singleEnded" | "differential" // default "singleEnded",
  capESeries //: 'E6' | 'E12' | 'E24' | 'E48' | 'E96', only used for Sallen Key Lowpass
) {
  let _implementation = canonicalizeImplementation(implementation);

  if (!componentCalculators[input]) {
    return;
  }
  if (!componentCalculators[input][_implementation]) {
    return;
  }
  if (!componentCalculators[input][_implementation][type]) {
    return;
  }
  if (gain === 0) {
    throw 'Gain must be non-zero';
  }

  let comps = componentCalculators[input][_implementation][type](cGov, fp, q, gain, rGainOrCCross, capESeries);

  return {
    r1: comps.r1,
    r2: comps.r2,
    r3: comps.r3,
    r4: comps.r4,
    r5: comps.r5,
    c1: comps.c1,
    c2: comps.c2,
    c3: comps.c3,
    l1: comps.l1,

    r6: comps.r6,
    r7: comps.r7,
    r8: comps.r8,
    r11: comps.r11,
    r12: comps.r12,
    r15: comps.r15,
    c4: comps.c4,
    c11: comps.c11,
    c12: comps.c12,
    c13: comps.c13,
    l2: comps.l2,
    l3: comps.l3,
  };
}

/**
 * stageSpecsFromComponents - Get the stage gain, fp, q, fz based on circuit components, filter type and filter implementation
 *
 * @param {"lowpass"|"bandpass"|"highpass"} type - The type of filter
 * @param {"sallenKey" | "multipleFeedback" | "activeFirstOrder" | "activeInductor" } implementation - The filter implementation used
 * @param {FilterPassives} filterComponents - Set of resistors, capacitors and inductors
 * @param {number} opAmpGBW - GBW. Value taken from the selected opAmp
 * @returns {FilterStageSpecs | undefined} - Filter stage type, order, gain, fp, fz, q
 */

export function stageSpecsFromComponents(
  type, //: "lowpass"|"bandpass"|"highpass"
  implementation, //: "multipleFeedback" | "sallenKey" | "activeFirstOrder" | "activeInductor",
  filterComponents, // Set of resistors, capacitors and inductors
  opAmpGBW, // Gain bandwidth of the opamp of the circuit
  input = 'singleEnded'
) {
  if (!specsCalculatorsFromComponents[input]) {
    return;
  }
  if (!specsCalculatorsFromComponents[input][implementation]) {
    return;
  }
  if (!specsCalculatorsFromComponents[input][implementation][type]) {
    return;
  }

  let specs = specsCalculatorsFromComponents[input][implementation][type](filterComponents, opAmpGBW);

  if (!specs) {
    return;
  }

  return {
    order: specs.order,
    fp: specs.fp, //pole frequency
    q: specs.q, //peaking
    fz: specs.fz, //zero frequency
    gain: specs.gain, //in V/V
    type: type,
  };
}
